import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { CacheKey } from '../constants/cachekey.constants';
import { ActiveSessionService } from '../services/active-session.service';
import { CacheService } from '../services/cache.service';
import { CommonService } from '../services/common.service';
import { ActionItemResponse } from '../interfaces/action-item.interface';
import { ActionType } from '../enums/document-type.enum';
import { MemberAcknowledgementRequest } from '../interfaces/member-acknowledgement-request.interface';
import { MyActionCenterModalComponentSettings } from '../component-settings';
import { AppModalDirective } from '../../common/directives/app-modal.directive';
import { saveAs as importedSaveAs } from 'file-saver';
import { DocumentType } from '../../common/enums/document-type.enum';
/**
 * @description this component used to display the account
 * details of user
 * @date 2018-09-20
 * @export
 * @class MyActionCenterModalComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-myaction-center-modal',
  templateUrl: '../templates/template1/views/app-myaction-center-modal.component.html',
  styleUrls: ['../templates/template1/themes/default/less/app-myaction-center-modal.component.less']
})

export class MyActionCenterModalComponent implements OnInit {

  @ViewChild('myActionCenterModal', { static: false }) public myActionCenterModal: AppModalDirective;
  macComponentSettings: MyActionCenterModalComponentSettings = new MyActionCenterModalComponentSettings();

  constructor(
    private _activeSessionService: ActiveSessionService,
    private _cacheService: CacheService,
    private _commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.loadDefaultSettings();
  }
  /**
   * @description this method will used to load default settings of userinfo
   * related to the account
   * @date 2018-09-20
   * @private
   * @memberof MyActionCenterModalComponent
   */
  private loadDefaultSettings(): void {
    this.macComponentSettings.isoCountryCode = this._cacheService.getCookieValue(CacheKey.countryCode);
    this.macComponentSettings.languageCode = this._cacheService.getCookieValue(CacheKey.languageCode);
    this.macComponentSettings.userInfo = this._cacheService.get(CacheKey.UserInfo);
    this.macComponentSettings.store = this._cacheService.get(CacheKey.StoreConfiguration);
    // Show or Hide My action center modal
    this.macComponentSettings.impersonatedUser = this._cacheService.get(CacheKey.ImpersonatedUser);
    if (this.macComponentSettings.userInfo) {
      this.getMemberActionItems();
    }
  }

  /**
   * @description To get the member specific action items
   * @date 2018-10-05
   * @memberof MyActionCenterModalComponent
   */
  getMemberActionItems(): void {
    this._commonService.getMemberActionItems(this.macComponentSettings.isoCountryCode,
      this.macComponentSettings.userInfo.memberId, this.macComponentSettings.languageCode)
      .subscribe(res => {
        if (res && res[0].body && res[0].body.length > 0) {
          this.macComponentSettings.actionsItems = res[0].body;
          // filter based on document accepted status
          this.macComponentSettings.actionsItems = this.macComponentSettings.actionsItems
            .filter(actionItem => !actionItem.acceptedStatus);
          this.filterBasedOnCurrentDocumentStatus();
          if (this.macComponentSettings.actionsItems.length > 0) {
            this.myActionCenterModal.show();
            this.macComponentSettings.actionsItems.forEach(item => {
              if (document.body.classList.contains('ie')) {
                if (item.fileTypeId === DocumentType.Word) {
                  item.displayName = item.displayName + '.' + 'doc';
                }
                if (item.fileTypeId === DocumentType.PDF) {
                  item.displayName = item.displayName + '.' + 'pdf';
                }
              }
              item.isCollapsed = true;
              item.isAcceptOrSkip = false;
              item.acceptClicked = false;
              item.skipClicked = false;
            });
            this.macComponentSettings.actionsItems[0].isCollapsed = false;
          }
        }
      }, (error: any) => {
        this.macComponentSettings.translateParams = this._commonService.handleError(error);
      });
  }

  /**
   * Filter acknowledged documents from documents for a member
   * filterDocumentsBasedOnDocumentStatus
   * @memberof MyActionCenterModalComponent
   */
  filterBasedOnCurrentDocumentStatus(): void {
    const documentStatus = this._cacheService.get(CacheKey.DocumentStatus);
    if (documentStatus && documentStatus.length > 0) {
      this.macComponentSettings.documentStates = documentStatus;
      const ids = documentStatus.map(item => item.id);
      this.macComponentSettings.actionsItems = this.macComponentSettings.actionsItems
        .filter(actionItem => ids.indexOf(actionItem.documentLibraryVersionId) === -1);
    }
  }

  /**
     * @description Member Action to Accept the document
     * @date 2018-10-10
     * @memberof MyActionCenterModalComponent
     */
  acceptDocument(actionItem: ActionItemResponse): void {
    this.memberAknowledgement(actionItem, ActionType.Accepted);
  }

  /**
   * @description Member Action to skip the document
   * @date 2018-10-10
   * @memberof MyActionCenterModalComponent
   */
  skipDocument(actionItem: ActionItemResponse): void {
    this.memberAknowledgement(actionItem, ActionType.Skipped);
    this.getAdminToken(actionItem);
  }

  /**
     * @description member acknowledgement on the action Item
     * @date 2018-10-10
     * @param {ActionItemResponse} actionItem
     * @param {ActionType} actionType
     * @memberof MyActionCenterModalComponent
     */
  memberAknowledgement(actionItem: ActionItemResponse, actionType: ActionType): void {
    if (actionType === 'Accepted') {
      actionItem.acceptClicked = true;
    }
    if (actionType === 'Skipped') {
      actionItem.skipClicked = true;
    }
    const memberAcknowledgementRequest: MemberAcknowledgementRequest = this.constructMemberAckRequest(actionItem, actionType);
    this._commonService.customerAknowledgement(memberAcknowledgementRequest).subscribe(response => {
      actionItem.isAcceptOrSkip = true;
      this.validateActionItems();
    });
  }

  /**
   * @description Validate action items for show or hide My action center modal
   * @date 2018-10-11
   * validateActionItems
   * @memberof MyActionCenterModalComponent
   */
  validateActionItems(): void {
    this.macComponentSettings.actionsItems = this.macComponentSettings.actionsItems.filter(actionItem => !actionItem.isAcceptOrSkip);
    if (this.macComponentSettings.actionsItems && this.macComponentSettings.actionsItems.length > 0) {
      this.macComponentSettings.actionsItems[0].isCollapsed = false;
    }
    if (!this.macComponentSettings.actionsItems || this.macComponentSettings.actionsItems.length === 0) {
      this.myActionCenterModal.hide();
      this._cacheService.set(CacheKey.MyActionCenterModalDisplayed, true);
    }
  }

  /**
     * @description Construct Member Action Request
     * @date 2018-10-10
     * @param {ActionItemResponse} actionItem
     * @param {ActionType} actionType
     * @returns {MemberAcknowledgementRequest}
     * @memberof MyActionCenterModalComponent
     */
  constructMemberAckRequest(actionItem: ActionItemResponse, actionType: ActionType): MemberAcknowledgementRequest {
    const memberAcknowledgementRequest: MemberAcknowledgementRequest = {
      firstName: this.macComponentSettings.userInfo.firstName,
      lastName: this.macComponentSettings.userInfo.lastName,
      memberId: this.macComponentSettings.userInfo.memberId,
      title: actionItem.displayName,
      recordId: actionItem.documentLibraryId,
      documentLibraryVersionId: actionItem.documentLibraryVersionId,
      acknowledgementType: actionType,
      memberType: this.macComponentSettings.userInfo.memberTypeId.toString()
    };
    return memberAcknowledgementRequest;
  }

  /** keycloak changes */
  getAdminToken(actionItem: ActionItemResponse) {
    this._commonService.getAdminToken().subscribe(response => {
      if (response) {
       // this.macComponentSettings.adminToken = response.adminToken;
        const documentState = {
          'id': actionItem.documentLibraryVersionId,
          'skipped': true
        };
        this.macComponentSettings.documentStates.push(documentState);
        this._commonService.saveUserActionsOnActionCenter(this.macComponentSettings.documentStates,
          this.macComponentSettings.adminToken, this.macComponentSettings.userInfo.userName)
          .subscribe(responseData => {
          });
      }
    });
  }

  /**
   * @description this method will be used to check the document
   * @date 2019-01-03
   * @memberof ActionCenterComponent
   */
  collapseDocument(actionItem: ActionItemResponse): void {
    const actionItems: ActionItemResponse[] = [];
    this.macComponentSettings.actionsItems.forEach(item => {
      if (actionItem === item) {
        item.isCollapsed = !actionItem.isCollapsed;
      } else {
        item.isCollapsed = true;
      }
      actionItems.push(item);
    });
    this.macComponentSettings.actionsItems = actionItems;
  }

  /**
  * @description this mwethod will be used to download document
  * @date 2019-01-03
  * @memberof ActionCenterComponent
  */
  downloadFile(documentUrl: string, name: string): void {
    if (documentUrl) {
      this._commonService.downloadFile(documentUrl).subscribe(blob => {
        importedSaveAs(blob, name);
      });
    }
  }
}
